import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MergeIcon from '@mui/icons-material/MergeType';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useRTL } from '@hooks/useIsRTL';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    maxWidth: '520px',
    width: '100%',
    padding: '0',
    margin: '16px',
  },
});

const CloseButtonContainer = styled(Box)({
  position: 'absolute',
  top: '16px',
  right: '16px',
});

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '48px 32px 32px',
});

const IconContainer = styled(Box)({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  backgroundColor: '#f3f0ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
});

const Title = styled(Typography)({
  fontSize: '20px',
  fontWeight: 700,
  color: '#262626',
  marginBottom: '12px',
  textAlign: 'center',
});

const Description = styled(Typography)({
  fontSize: '14px',
  color: '#666666',
  marginBottom: '24px',
  textAlign: 'center',
  lineHeight: '22px',
});

const WhenToUseContainer = styled(Box)({
  width: '100%',
  backgroundColor: '#faf5ff',
  borderRadius: '12px',
  padding: '16px',
  marginBottom: '16px',
  border: '1px solid #f3e8ff',
});

const WhenToUseTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  marginBottom: '12px',
});

const WhenToUseItem = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  marginBottom: '8px',
  '&:last-child': {
    marginBottom: 0,
  },
});

const WhenToUseText = styled(Typography)({
  fontSize: '13px',
  color: '#666666',
  lineHeight: '20px',
});

const InfoBanner = styled(Box)({
  width: '100%',
  backgroundColor: 'rgba(0, 106, 255, 0.08)',
  borderRadius: '12px',
  padding: '12px 16px',
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  border: '1px solid rgba(0, 106, 255, 0.2)',
});

const InfoText = styled(Typography)({
  fontSize: '13px',
  color: '#003d99',
  lineHeight: '20px',
  flex: 1,
});

const StartButton = styled(Button)({
  width: '100%',
  backgroundColor: '#5b3fff',
  color: '#ffffff',
  padding: '14px 24px',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  '&:hover': {
    backgroundColor: '#4a2fe8',
  },
});

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onStart: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = memo(({ open, onClose, onStart }) => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();

  const handleStart = () => {
    onStart();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <CloseButtonContainer>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: '#666666' }} />
        </IconButton>
      </CloseButtonContainer>
      <DialogContent sx={{ padding: 0 }}>
        <ContentContainer>
          <IconContainer>
            <MergeIcon sx={{ fontSize: 32, color: '#5b3fff' }} />
          </IconContainer>
          
          <Title>
            {t('CHANNELS.UNITS_MAPPING.MODAL_TITLE', 'ما هو دمج الوحدات؟')}
          </Title>
          
          <Description>
            {t('CHANNELS.UNITS_MAPPING.MODAL_DESCRIPTION', 'دمج الوحدات يسمح لك بربط نفس الوحدة الموجودة على منصات مختلفة (مثل Airbnb وBooking وGathern) في وحدة واحدة موحدة داخل مسؤول.')}
          </Description>

          <WhenToUseContainer>
            <WhenToUseTitle>
              {t('CHANNELS.UNITS_MAPPING.MODAL_WHEN_TO_USE_TITLE', 'متى تستخدم دمج الوحدات؟')}
            </WhenToUseTitle>
            
            <WhenToUseItem>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#5b3fff', mt: '2px' }} />
              <WhenToUseText>
                {t('CHANNELS.UNITS_MAPPING.MODAL_WHEN_TO_USE_1', 'عندما تكون نفس الوحدة معروضة على أكثر من منصة')}
              </WhenToUseText>
            </WhenToUseItem>
            
            <WhenToUseItem>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#5b3fff', mt: '2px' }} />
              <WhenToUseText>
                {t('CHANNELS.UNITS_MAPPING.MODAL_WHEN_TO_USE_2', 'لإدارة التقويم والأسعار من مكان واحد')}
              </WhenToUseText>
            </WhenToUseItem>
            
            <WhenToUseItem>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#5b3fff', mt: '2px' }} />
              <WhenToUseText>
                {t('CHANNELS.UNITS_MAPPING.MODAL_WHEN_TO_USE_3', 'لتجنب الحجوزات المزدوجة')}
              </WhenToUseText>
            </WhenToUseItem>
          </WhenToUseContainer>

          <InfoBanner>
            <InfoOutlinedIcon sx={{ fontSize: 20, color: '#006aff' }} />
            <InfoText>
              {t('CHANNELS.UNITS_MAPPING.MODAL_INFO_TEXT', 'بعد دمج الوحدات، سيتم مزامنة التوفر والحجوزات تلقائياً بين جميع المنصات المرتبطة.')}
            </InfoText>
          </InfoBanner>

          <StartButton onClick={handleStart}>
            {t('CHANNELS.UNITS_MAPPING.MODAL_START_BUTTON', 'ابدأ الآن')}
            {isRTL ? (
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            ) : (
              <ArrowForwardIcon sx={{ fontSize: 20 }} />
            )}
          </StartButton>
        </ContentContainer>
      </DialogContent>
    </StyledDialog>
  );
});

OnboardingModal.displayName = 'OnboardingModal';

export default OnboardingModal;
